import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { CurrencyDisplay } from "./CurrencySettings";
import { 
  Calculator, 
  TrendingUp, 
  TrendingDown, 
  CheckCircle, 
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Receipt,
  Banknote,
  FileText,
  Download,
  Plus,
  Settings,
  Lock,
  Info,
  Calendar,
  FileDown,
  ClipboardCheck
} from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

// Cash Ledger Start Date - must match backend constant
const CASH_LEDGER_START_DATE = "2026-01-01";

const CashReconciliationReport = ({ user }) => {
  // Default to current month, but ensure start date is not before ledger start
  const today = new Date();
  
  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  const firstOfMonth = formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1));
  const lastOfMonth = formatDateLocal(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  
  // Ensure default start date is not before ledger start date
  const defaultStartDate = firstOfMonth < CASH_LEDGER_START_DATE ? CASH_LEDGER_START_DATE : firstOfMonth;
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(lastOfMonth);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [ledgerConfig, setLedgerConfig] = useState(null);
  const [reconciliations, setReconciliations] = useState([]);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileDetailOpen, setReconcileDetailOpen] = useState(null);
  const [restaurantBalances, setRestaurantBalances] = useState([]);
  const [actualCash, setActualCash] = useState({});
  const [reconcileNotes, setReconcileNotes] = useState({});
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupAmounts, setSetupAmounts] = useState({});
  const [setupEffectiveDate, setSetupEffectiveDate] = useState("2026-08-31");
  const [setupSubmitting, setSetupSubmitting] = useState(false);
  const [newAdjustment, setNewAdjustment] = useState({
    adjustment_type: "opening",
    amount: "",
    date: "2025-12-31",  // Opening balance represents cash as of Dec 31, 2025
    notes: "Physical cash balance as of December 31, 2025"
  });

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport();
    }
  }, []);

  useEffect(() => {
    fetchLedgerConfig();
    fetchReconciliations();
    fetchSetupStatus();
  }, []);

  const fetchLedgerConfig = async () => {
    try {
      const response = await axiosInstance.get("/cash-ledger/config");
      setLedgerConfig(response.data);
    } catch (error) {
      console.error("Error fetching ledger config:", error);
    }
  };

  const fetchReconciliations = async () => {
    try {
      const response = await axiosInstance.get("/cash-reconciliation/monthly");
      setReconciliations(response.data);
    } catch (error) {
      console.error("Error fetching reconciliations:", error);
    }
  };

  const openNewReconciliation = async () => {
    try {
      const res = await axiosInstance.get("/restaurant-cash/balances");
      setRestaurantBalances(res.data);
      const initial = {};
      const notes = {};
      res.data.forEach(b => { initial[b.restaurant_id] = ""; notes[b.restaurant_id] = ""; });
      setActualCash(initial);
      setReconcileNotes(notes);
      setReconcileOpen(true);
    } catch (error) {
      toast.error("Failed to fetch restaurant balances");
    }
  };

  const reconcileTotalDiff = restaurantBalances.reduce((sum, b) => {
    const actual = parseFloat(actualCash[b.restaurant_id]);
    if (isNaN(actual)) return sum;
    return sum + (actual - b.current_balance);
  }, 0);

  const reconcileFilledCount = restaurantBalances.filter(b => actualCash[b.restaurant_id] !== "").length;

  const handleReconcileSubmit = async () => {
    if (reconcileFilledCount === 0) { toast.error("Enter actual cash for at least one restaurant"); return; }
    setReconcileSubmitting(true);
    try {
      const entries = restaurantBalances
        .filter(b => actualCash[b.restaurant_id] !== "")
        .map(b => ({
          restaurant_id: b.restaurant_id,
          restaurant_name: b.restaurant_name,
          system_balance: b.current_balance,
          actual_balance: parseFloat(actualCash[b.restaurant_id]) || 0,
          difference: (parseFloat(actualCash[b.restaurant_id]) || 0) - b.current_balance,
          notes: reconcileNotes[b.restaurant_id] || "",
        }));

      await axiosInstance.post("/cash-reconciliation/monthly", {
        date: new Date().toISOString().split('T')[0],
        entries,
      });

      toast.success("Monthly reconciliation saved!");
      setReconcileOpen(false);
      fetchReconciliations();
    } catch (error) {
      toast.error("Failed to save reconciliation");
    } finally {
      setReconcileSubmitting(false);
    }
  };


  // Setup functions
  const fetchSetupStatus = async (dateOverride) => {
    try {
      const params = dateOverride ? `?as_of_date=${dateOverride}` : "";
      const res = await axiosInstance.get(`/restaurant-cash/setup-status${params}`);
      setSetupStatus(res.data);
    } catch (error) {
      console.error("Error fetching setup status:", error);
    }
  };

  const openSetupWizard = () => {
    if (!setupStatus) return;
    const amounts = {};
    setupStatus.restaurants.forEach(r => {
      const existing = setupStatus.restaurant_openings[r.id];
      amounts[r.id] = existing ? String(existing.opening_balance) : "";
    });
    setSetupAmounts(amounts);
    setSetupEffectiveDate(setupStatus.effective_date || "2026-08-31");
    setSetupOpen(true);
  };

  // Re-fetch balance when effective date changes in the wizard
  useEffect(() => {
    if (setupOpen && setupEffectiveDate) {
      fetchSetupStatus(setupEffectiveDate);
    }
  }, [setupEffectiveDate, setupOpen]);

  const setupTotal = Object.values(setupAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const setupGlobal = setupStatus?.global_opening_balance || 0;
  const setupRemaining = setupGlobal - setupTotal;
  const setupValid = Math.abs(setupRemaining) < 0.01;

  const handleSetupSubmit = async () => {
    if (!setupValid) {
      toast.error(`Remaining ₹${setupRemaining.toFixed(2)} must be allocated`);
      return;
    }
    setSetupSubmitting(true);
    try {
      const entries = setupStatus.restaurants.map(r => ({
        restaurant_id: r.id,
        opening_balance: parseFloat(setupAmounts[r.id]) || 0,
      }));
      await axiosInstance.post("/restaurant-cash/setup-opening-balances", {
        effective_date: setupEffectiveDate,
        entries,
      });
      toast.success("Opening balances saved! Per-restaurant tracking is now active.");
      setSetupOpen(false);
      fetchSetupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save setup");
    } finally {
      setSetupSubmitting(false);
    }
  };


  // Handle start date change - enforce minimum date
  const handleStartDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate < CASH_LEDGER_START_DATE) {
      toast.error(`Start date cannot be before ${CASH_LEDGER_START_DATE}`);
      setStartDate(CASH_LEDGER_START_DATE);
    } else {
      setStartDate(newDate);
    }
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/cash-reconciliation?start_date=${startDate}&end_date=${endDate}`);
      setReport(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view reconciliation reports");
      } else {
        toast.error("Failed to fetch reconciliation report");
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdjustment = async () => {
    if (!newAdjustment.amount || parseFloat(newAdjustment.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      await axiosInstance.post("/cash-adjustments", {
        ...newAdjustment,
        amount: parseFloat(newAdjustment.amount)
      });
      toast.success("Opening Balance set successfully!");
      setNewAdjustment({ 
        adjustment_type: "opening", 
        amount: "", 
        date: "2025-12-31", 
        notes: "Physical cash balance as of December 31, 2025" 
      });
      fetchLedgerConfig();
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add opening balance");
    }
  };

  const handleDeleteAdjustment = async (id) => {
    try {
      await axiosInstance.delete(`/cash-adjustments/${id}`);
      toast.success("Opening Balance deleted");
      fetchLedgerConfig();
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete opening balance");
    }
  };

  const exportToCSV = () => {
    if (!report) return;

    let csvContent = `Cash Reconciliation Report\n`;
    csvContent += `Ledger Start Date: ${CASH_LEDGER_START_DATE}\n`;
    csvContent += `Period: ${report.start_date} to ${report.end_date}\n\n`;
    
    csvContent += `SUMMARY\n`;
    csvContent += `Opening Balance,${report.opening_balance}\n`;
    csvContent += `Total Cash Revenue,${report.total_cash_revenue}\n`;
    csvContent += `Total Cash Expenses,${report.total_cash_expenses}\n`;
    csvContent += `Total Cash Handovers,${report.total_cash_handovers}\n`;
    csvContent += `Net Cash Movement,${report.net_cash_movement}\n`;
    csvContent += `Closing Balance,${report.closing_balance}\n\n`;

    csvContent += `CASH REVENUE BREAKDOWN\n`;
    csvContent += `Date,Restaurant,Amount\n`;
    report.cash_revenue_breakdown.forEach(item => {
      csvContent += `${item.date},${item.restaurant_name},${item.amount}\n`;
    });

    csvContent += `\nCASH EXPENSE ENTRIES\n`;
    csvContent += `Date,Restaurant,Category,Description,Vendor,Amount\n`;
    report.cash_expense_entries.forEach(item => {
      csvContent += `${item.date},${item.restaurant_name},${item.category},"${item.description}",${item.vendor},${item.amount}\n`;
    });

    csvContent += `\nCASH HANDOVER ENTRIES\n`;
    csvContent += `Date,Handed Over By,Handed Over To,Amount,Remarks\n`;
    report.handover_entries.forEach(item => {
      csvContent += `${item.date},${item.handed_over_by},${item.handed_over_to},${item.amount},"${item.remarks}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cash_reconciliation_${report.start_date}_to_${report.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("CSV exported successfully!");
  };

  const exportToPDF = () => {
    if (!report) return;

    const doc = new jsPDF('portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text("Cash Reconciliation Report", pageWidth / 2, 20, { align: 'center' });
    
    // Period info
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Ledger Start Date: ${CASH_LEDGER_START_DATE}`, 14, 30);
    doc.text(`Report Period: ${report.start_date} to ${report.end_date}`, 14, 36);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 42);
    
    // Summary Box
    doc.setFillColor(240, 240, 245);
    doc.rect(14, 48, pageWidth - 28, 50, 'F');
    doc.setDrawColor(100, 100, 150);
    doc.rect(14, 48, pageWidth - 28, 50, 'S');
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text("SUMMARY", 20, 56);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    // Left column
    doc.text(`Opening Balance:`, 20, 66);
    doc.text(`Cash Revenue:`, 20, 74);
    doc.text(`Cash Expenses:`, 20, 82);
    
    // Right column
    doc.text(`Cash Handovers:`, 110, 66);
    doc.text(`Net Movement:`, 110, 74);
    
    // Values - right aligned
    doc.setFont(undefined, 'bold');
    doc.text(`₹${report.opening_balance.toLocaleString('en-IN')}`, 95, 66, { align: 'right' });
    doc.setTextColor(0, 128, 0);
    doc.text(`₹${report.total_cash_revenue.toLocaleString('en-IN')}`, 95, 74, { align: 'right' });
    doc.setTextColor(200, 0, 0);
    doc.text(`₹${report.total_cash_expenses.toLocaleString('en-IN')}`, 95, 82, { align: 'right' });
    
    doc.setTextColor(128, 0, 128);
    doc.text(`₹${report.total_cash_handovers.toLocaleString('en-IN')}`, pageWidth - 20, 66, { align: 'right' });
    doc.setTextColor(report.net_cash_movement >= 0 ? 0 : 200, report.net_cash_movement >= 0 ? 128 : 0, 0);
    doc.text(`₹${report.net_cash_movement.toLocaleString('en-IN')}`, pageWidth - 20, 74, { align: 'right' });
    
    // Closing Balance - highlighted
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.text(`CLOSING BALANCE:`, 20, 92);
    doc.setFontSize(14);
    doc.setTextColor(0, 100, 0);
    doc.text(`₹${report.closing_balance.toLocaleString('en-IN')}`, pageWidth - 20, 92, { align: 'right' });
    
    let yPos = 110;
    
    // Cash Revenue Table
    if (report.cash_revenue_breakdown && report.cash_revenue_breakdown.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text("Cash Revenue Breakdown", 14, yPos);
      
      autoTable(doc, {
        startY: yPos + 4,
        head: [['Date', 'Restaurant', 'Amount']],
        body: report.cash_revenue_breakdown.map(item => [
          item.date,
          item.restaurant_name,
          `₹${item.amount.toLocaleString('en-IN')}`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [34, 139, 34], fontSize: 9 },
        styles: { fontSize: 8 },
        columnStyles: { 2: { halign: 'right' } }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Cash Expenses Table
    if (report.cash_expense_entries && report.cash_expense_entries.length > 0) {
      // Check if we need a new page
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text("Cash Expense Entries", 14, yPos);
      
      autoTable(doc, {
        startY: yPos + 4,
        head: [['Date', 'Restaurant', 'Category', 'Vendor', 'Amount']],
        body: report.cash_expense_entries.map(item => [
          item.date,
          item.restaurant_name,
          item.category,
          item.vendor || '-',
          `₹${item.amount.toLocaleString('en-IN')}`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [178, 34, 34], fontSize: 9 },
        styles: { fontSize: 8 },
        columnStyles: { 4: { halign: 'right' } }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Cash Handovers Table
    if (report.handover_entries && report.handover_entries.length > 0) {
      // Check if we need a new page
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text("Cash Handover Entries", 14, yPos);
      
      autoTable(doc, {
        startY: yPos + 4,
        head: [['Date', 'Handed By', 'Handed To', 'Amount', 'Remarks']],
        body: report.handover_entries.map(item => [
          item.date,
          item.handed_over_by,
          item.handed_over_to,
          `₹${item.amount.toLocaleString('en-IN')}`,
          item.remarks || '-'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [128, 0, 128], fontSize: 9 },
        styles: { fontSize: 8 },
        columnStyles: { 3: { halign: 'right' } }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }
    
    doc.save(`cash_reconciliation_${report.start_date}_to_${report.end_date}.pdf`);
    toast.success("PDF exported successfully!");
  };

  // Check if user is admin or superuser (for Opening Balance management)
  const canManageOpeningBalance = user?.role === 'admin' || user?.role === 'superuser';

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="shadow-xl border-0 bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Calculator className="w-6 h-6" />
                Cash Reconciliation Dashboard
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                Rolling cash ledger starting from {CASH_LEDGER_START_DATE}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {canManageOpeningBalance && (
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setAdjustmentDialog(true)}
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                Opening Balance
                {ledgerConfig?.opening_balance_exists && (
                  <CheckCircle className="w-3 h-3 text-green-600" />
                )}
              </Button>
              )}
              {report && (
                <>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={exportToCSV}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    CSV
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={exportToPDF}
                    className="gap-2"
                  >
                    <FileDown className="w-4 h-4" />
                    PDF
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Ledger Start Date Notice */}
          {startDate < CASH_LEDGER_START_DATE && (
            <Alert className="mb-4 bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Note:</strong> The cash ledger starts from {CASH_LEDGER_START_DATE}. Transactions before this date are not included in calculations.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Quick Select Month</Label>
              <Input
                type="month"
                onChange={(e) => {
                  if (e.target.value) {
                    const [year, month] = e.target.value.split("-");
                    const y = parseInt(year);
                    const m = parseInt(month);
                    const firstDay = `${year}-${month}-01`;
                    const lastDay = new Date(y, m, 0).getDate();
                    const endDay = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
                    const clampedStart = firstDay < CASH_LEDGER_START_DATE ? CASH_LEDGER_START_DATE : firstDay;
                    setStartDate(clampedStart);
                    setEndDate(endDay);
                  }
                }}
                className="w-44"
                data-testid="reconciliation-month-picker"
              />
            </div>
            <div className="text-xs text-gray-400 self-center pt-4">or</div>
            <div className="space-y-2">
              <Label>Start Date (Min: {CASH_LEDGER_START_DATE})</Label>
              <Input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                min={CASH_LEDGER_START_DATE}
                className="w-44"
                data-testid="reconciliation-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-44"
                data-testid="reconciliation-end-date"
              />
            </div>
            <Button 
              onClick={fetchReport} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* One-Time Setup Banner */}
      {setupStatus && !setupStatus.setup_completed && canManageOpeningBalance && (
        <Card className="border-2 border-amber-300 bg-amber-50 shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">One-Time Setup Required</p>
                  <p className="text-sm text-amber-700">
                    Distribute the carrying cash balance (<CurrencyDisplay amount={setupStatus.global_opening_balance} />) across restaurants to enable per-restaurant cash tracking.
                  </p>
                </div>
              </div>
              <Button onClick={openSetupWizard} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-setup-btn">
                Setup Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {setupStatus?.setup_completed && (
        <Card className="border border-green-200 bg-green-50/50">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Per-restaurant cash tracking active since {setupStatus.effective_date}</span>
            </div>
            {canManageOpeningBalance && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-green-700 hover:text-green-900" data-testid="edit-allocation-btn">
                    Edit Allocation
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      Edit Opening Balance Allocation?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-gray-600">
                      Changing restaurant opening balances will affect all per-restaurant cash calculations going forward. Only proceed if you are sure the current allocation is incorrect.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={openSetupWizard} className="bg-amber-600 hover:bg-amber-700">
                      Continue to Edit
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardContent>
        </Card>
      )}

      {/* Setup Wizard Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-600" />
              Per-Restaurant Opening Balance Setup
            </DialogTitle>
            <DialogDescription>
              Distribute the carrying cash balance across restaurants. The total must match the cash available as of the effective date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">Cash Balance to Distribute</span>
                <span className="text-lg font-bold text-blue-900"><CurrencyDisplay amount={setupGlobal} /></span>
              </div>
              {setupStatus?.carrying_balance_details && (
                <div className="text-xs text-blue-600 space-y-0.5 pt-1 border-t border-blue-200">
                  <div className="flex justify-between"><span>Original Opening</span><span><CurrencyDisplay amount={setupStatus.original_opening_balance} /></span></div>
                  <div className="flex justify-between"><span>+ Cash Revenue (before effective date)</span><span><CurrencyDisplay amount={setupStatus.carrying_balance_details.revenue} /></span></div>
                  <div className="flex justify-between"><span>− Cash Expenses</span><span><CurrencyDisplay amount={setupStatus.carrying_balance_details.expenses} /></span></div>
                  <div className="flex justify-between"><span>− Cash Handovers</span><span><CurrencyDisplay amount={setupStatus.carrying_balance_details.handovers} /></span></div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Effective Date (balances as of)</Label>
              <Input type="date" value={setupEffectiveDate} onChange={(e) => setSetupEffectiveDate(e.target.value)} className="w-48" data-testid="setup-effective-date" />
            </div>

            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {setupStatus?.restaurants.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-gray-50/50" data-testid={`setup-row-${r.id}`}>
                  <span className="flex-1 text-sm font-medium">{r.name}</span>
                  <div className="w-36 relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">₹</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0"
                      value={setupAmounts[r.id] || ""}
                      onChange={(e) => setSetupAmounts(prev => ({ ...prev, [r.id]: e.target.value }))}
                      className="pl-7 h-9 text-sm"
                      data-testid={`setup-amount-${r.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-3 rounded-lg border-2 ${setupValid ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Allocated</span>
                <span className="font-bold"><CurrencyDisplay amount={setupTotal} /></span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-medium">Remaining</span>
                <span className={`font-bold ${setupValid ? 'text-green-600' : 'text-red-600'}`}>
                  <CurrencyDisplay amount={setupRemaining} />
                </span>
              </div>
              {setupValid && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Allocation matches global balance</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button>
            <Button onClick={handleSetupSubmit} disabled={setupSubmitting || !setupValid} className="bg-amber-600 hover:bg-amber-700" data-testid="save-setup-btn">
              {setupSubmitting ? "Saving..." : "Save & Activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-lg text-gray-500">Loading reconciliation data...</div>
        </div>
      )}

      {report && !loading && (
        <>
          {/* Summary Cards - 2 rows */}
          <div className="grid gap-4 md:grid-cols-3">
            {/* Opening Balance */}
            <Card className="shadow-lg border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-blue-500" />
                  Opening Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.opening_balance} 
                  className="text-2xl font-bold text-blue-600" 
                />
                <p className="text-xs text-gray-500 mt-1">Balance before {report.start_date}</p>
              </CardContent>
            </Card>

            {/* Cash Revenue */}
            <Card className="shadow-lg border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4 text-green-500" />
                  Cash Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.total_cash_revenue} 
                  className="text-2xl font-bold text-green-600" 
                />
                <p className="text-xs text-gray-500 mt-1">{report.cash_revenue_breakdown.length} entries</p>
              </CardContent>
            </Card>

            {/* Cash Expenses */}
            <Card className="shadow-lg border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-red-500" />
                  Cash Expenses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.total_cash_expenses} 
                  className="text-2xl font-bold text-red-600" 
                />
                <p className="text-xs text-gray-500 mt-1">{report.cash_expense_entries.length} entries</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Cash Handovers */}
            <Card className="shadow-lg border-l-4 border-l-purple-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-purple-500" />
                  Cash Handovers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.total_cash_handovers} 
                  className="text-2xl font-bold text-purple-600" 
                />
                <p className="text-xs text-gray-500 mt-1">{report.handover_entries.length} handovers</p>
              </CardContent>
            </Card>

            {/* Net Movement */}
            <Card className="shadow-lg border-l-4 border-l-orange-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-orange-500" />
                  Net Cash Movement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.net_cash_movement} 
                  className={`text-2xl font-bold ${report.net_cash_movement >= 0 ? 'text-green-600' : 'text-red-600'}`} 
                />
                <p className="text-xs text-gray-500 mt-1">Revenue - Expenses - Handovers</p>
              </CardContent>
            </Card>

            {/* Closing Balance */}
            <Card className="shadow-lg border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500" />
                  Closing Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CurrencyDisplay 
                  amount={report.closing_balance} 
                  className="text-2xl font-bold text-indigo-600" 
                />
                <p className="text-xs text-gray-500 mt-1">Balance as of {report.end_date}</p>
              </CardContent>
            </Card>
          </div>

          {/* Reconciliation Formula */}
          <Card className="shadow-xl border-2 border-indigo-200 bg-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Cash Ledger Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 text-lg">
                <div className="flex justify-between py-2 border-b">
                  <span>Opening Balance</span>
                  <CurrencyDisplay amount={report.opening_balance} className="font-bold" />
                </div>
                <div className="flex justify-between py-2 border-b text-green-600">
                  <span>+ Cash Revenue</span>
                  <CurrencyDisplay amount={report.total_cash_revenue} className="font-bold" />
                </div>
                <div className="flex justify-between py-2 border-b text-red-600">
                  <span>− Cash Expenses</span>
                  <CurrencyDisplay amount={report.total_cash_expenses} className="font-bold" />
                </div>
                <div className="flex justify-between py-2 border-b text-purple-600">
                  <span>− Cash Handovers</span>
                  <CurrencyDisplay amount={report.total_cash_handovers} className="font-bold" />
                </div>
                <div className="flex justify-between py-3 text-xl bg-white rounded-lg px-4 mt-2">
                  <span className="font-bold">= Closing Balance</span>
                  <CurrencyDisplay amount={report.closing_balance} className="font-bold text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detail Tables */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Cash Revenue Breakdown */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Cash Revenue Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.cash_revenue_breakdown.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No cash revenue in this period</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Restaurant</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.cash_revenue_breakdown.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm">{item.date}</TableCell>
                            <TableCell className="text-sm">{item.restaurant_name}</TableCell>
                            <TableCell className="text-right font-medium text-green-600">
                              <CurrencyDisplay amount={item.amount} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cash Handover Details */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-purple-500" />
                  Cash Handover Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.handover_entries.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No handovers in this period</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>By → To</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.handover_entries.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm">{item.date}</TableCell>
                            <TableCell className="text-sm">{item.handed_over_by} → {item.handed_over_to}</TableCell>
                            <TableCell className="text-right font-medium text-purple-600">
                              <CurrencyDisplay amount={item.amount} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Cash Expenses Table */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Cash Expense Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {report.cash_expense_entries.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No cash expenses in this period</p>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Restaurant</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.cash_expense_entries.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-sm">{item.date}</TableCell>
                          <TableCell className="text-sm">{item.restaurant_name}</TableCell>
                          <TableCell className="text-sm">{item.category}</TableCell>
                          <TableCell className="text-sm max-w-[150px] truncate">{item.description}</TableCell>
                          <TableCell className="text-sm">{item.vendor}</TableCell>
                          <TableCell className="text-right font-medium text-red-600">
                            <CurrencyDisplay amount={item.amount} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== Monthly Reconciliation Section ===== */}
      <Card className="shadow-xl border-0">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardCheck className="w-5 h-5" />
                Monthly Cash Reconciliation
              </CardTitle>
              <CardDescription className="text-blue-100 mt-1">
                Compare actual cash counted at each restaurant with system balance
              </CardDescription>
            </div>
            <Button variant="secondary" size="sm" onClick={openNewReconciliation} className="gap-2" data-testid="new-reconciliation-btn">
              <Plus className="w-4 h-4" /> New Reconciliation
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {reconciliations.length === 0 ? (
            <p className="text-center text-gray-500 py-6">No reconciliations recorded yet. Click "New Reconciliation" to start.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reconciled By</TableHead>
                  <TableHead>Restaurants</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Net Difference</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reconciliations.map((r, idx) => {
                  const netDiff = r.entries.reduce((s, e) => s + (e.difference || 0), 0);
                  const hasDisc = r.status === "discrepancy";
                  return (
                    <TableRow key={r.id || idx} className="cursor-pointer hover:bg-blue-50/50" onClick={() => setReconcileDetailOpen(r)} data-testid={`reconciliation-row-${idx}`}>
                      <TableCell className="font-medium">{r.date}</TableCell>
                      <TableCell>{r.reconciled_by_name}</TableCell>
                      <TableCell>{r.entries.length}</TableCell>
                      <TableCell>
                        <Badge variant={hasDisc ? "destructive" : "default"} className={hasDisc ? "" : "bg-green-100 text-green-800 hover:bg-green-100"}>
                          {hasDisc ? "Discrepancy" : "Reconciled"}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${Math.abs(netDiff) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                        {netDiff >= 0 ? '+' : ''}<CurrencyDisplay amount={netDiff} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setReconcileDetailOpen(r); }}>View</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Reconciliation Dialog */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" /> New Monthly Reconciliation
            </DialogTitle>
            <DialogDescription>Count actual cash at each restaurant and enter below. System will highlight discrepancies.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {restaurantBalances.map(b => {
              const actual = parseFloat(actualCash[b.restaurant_id]);
              const hasValue = actualCash[b.restaurant_id] !== "";
              const diff = hasValue ? actual - b.current_balance : 0;
              const isMatch = hasValue && Math.abs(diff) < 0.01;
              const isShort = hasValue && diff < -0.01;
              const isExcess = hasValue && diff > 0.01;
              return (
                <div key={b.restaurant_id} className={`p-3 rounded-lg border ${isMatch ? 'border-green-200 bg-green-50/30' : isShort ? 'border-red-200 bg-red-50/30' : isExcess ? 'border-amber-200 bg-amber-50/30' : 'bg-gray-50/50'}`} data-testid={`reconcile-row-${b.restaurant_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{b.restaurant_name}</p>
                      <p className="text-xs text-gray-500">System: <CurrencyDisplay amount={b.current_balance} /></p>
                    </div>
                    <div className="w-36">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">₹</span>
                        <Input type="number" step="0.01" placeholder="Actual cash" value={actualCash[b.restaurant_id] || ""} onChange={(e) => setActualCash(prev => ({ ...prev, [b.restaurant_id]: e.target.value }))} className="pl-7 h-9 text-sm" data-testid={`reconcile-actual-${b.restaurant_id}`} />
                      </div>
                    </div>
                    <div className="w-28 text-right">
                      {hasValue && (
                        <div className="flex items-center justify-end gap-1">
                          {isMatch && <CheckCircle className="w-4 h-4 text-green-600" />}
                          {isShort && <AlertTriangle className="w-4 h-4 text-red-600" />}
                          {isExcess && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                          <span className={`text-sm font-semibold ${isMatch ? 'text-green-600' : isShort ? 'text-red-600' : 'text-amber-600'}`}>
                            {diff >= 0 ? '+' : ''}<CurrencyDisplay amount={diff} />
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="w-32">
                      <Input placeholder="Notes..." value={reconcileNotes[b.restaurant_id] || ""} onChange={(e) => setReconcileNotes(prev => ({ ...prev, [b.restaurant_id]: e.target.value }))} className="h-9 text-xs" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {reconcileFilledCount > 0 && (
            <div className="pt-3 border-t flex items-center justify-between text-sm">
              <span className="text-gray-500">{reconcileFilledCount} restaurant{reconcileFilledCount > 1 ? 's' : ''} reconciled</span>
              <span className={`font-bold ${Math.abs(reconcileTotalDiff) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                Net difference: {reconcileTotalDiff >= 0 ? '+' : ''}<CurrencyDisplay amount={reconcileTotalDiff} />
              </span>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReconcileOpen(false)}>Cancel</Button>
            <Button onClick={handleReconcileSubmit} disabled={reconcileSubmitting || reconcileFilledCount === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="save-reconciliation-btn">
              {reconcileSubmitting ? "Saving..." : "Save Reconciliation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconciliation Detail Dialog */}
      <Dialog open={!!reconcileDetailOpen} onOpenChange={(open) => !open && setReconcileDetailOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Reconciliation Report — {reconcileDetailOpen?.date}
            </DialogTitle>
          </DialogHeader>
          {reconcileDetailOpen && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div>
                  <p className="text-xs text-gray-500">Reconciled by</p>
                  <p className="font-medium text-sm">{reconcileDetailOpen.reconciled_by_name}</p>
                </div>
                <Badge variant={reconcileDetailOpen.status === "discrepancy" ? "destructive" : "default"} className={reconcileDetailOpen.status !== "discrepancy" ? "bg-green-100 text-green-800" : ""}>
                  {reconcileDetailOpen.status === "discrepancy" ? "Discrepancy" : "Reconciled"}
                </Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Restaurant</TableHead>
                    <TableHead className="text-right">System</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconcileDetailOpen.entries.map((e, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium text-sm">{e.restaurant_name}</TableCell>
                      <TableCell className="text-right text-sm"><CurrencyDisplay amount={e.system_balance} /></TableCell>
                      <TableCell className="text-right text-sm"><CurrencyDisplay amount={e.actual_balance} /></TableCell>
                      <TableCell className={`text-right text-sm font-bold ${Math.abs(e.difference) < 0.01 ? 'text-green-600' : e.difference < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                        {e.difference >= 0 ? '+' : ''}<CurrencyDisplay amount={e.difference} />
                        {e.notes && <p className="text-xs font-normal text-gray-500">{e.notes}</p>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-between p-3 rounded-lg bg-gray-100 font-bold">
                <span>Net Difference</span>
                <span className={(() => { const d = reconcileDetailOpen.entries.reduce((s,e) => s + (e.difference||0), 0); return Math.abs(d) < 0.01 ? 'text-green-600' : 'text-red-600'; })()}>
                  {(() => { const d = reconcileDetailOpen.entries.reduce((s,e) => s + (e.difference||0), 0); return (d >= 0 ? '+' : '') })()}<CurrencyDisplay amount={reconcileDetailOpen.entries.reduce((s,e) => s + (e.difference||0), 0)} />
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileDetailOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opening Balance / Adjustment Dialog */}
      <Dialog open={adjustmentDialog} onOpenChange={setAdjustmentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Cash Ledger Opening Balance
            </DialogTitle>
            <DialogDescription>
              Set the initial opening balance for the cash ledger. This represents the physical cash balance as of December 31, 2025.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Ledger Info Banner */}
            <Alert className="bg-blue-50 border-blue-200">
              <Calendar className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>Cash Ledger Start Date:</strong> {CASH_LEDGER_START_DATE}<br />
                <span className="text-sm">All transactions before this date are ignored. The opening balance represents physical cash as of Dec 31, 2025.</span>
              </AlertDescription>
            </Alert>

            {/* Show existing opening balance or form to add one */}
            {ledgerConfig?.opening_balance_exists ? (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-800">Opening Balance Set</h4>
                  {ledgerConfig?.opening_balance_locked && (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                      <Lock className="w-3 h-3 mr-1" />
                      Locked
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-2xl font-bold text-green-700">
                      <CurrencyDisplay amount={ledgerConfig.opening_balance?.amount || 0} />
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Effective Date</p>
                    <p className="font-medium">{ledgerConfig.opening_balance?.date || "2025-12-31"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="text-sm">{ledgerConfig.opening_balance?.notes || "Physical cash balance as of December 31, 2025"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Created By</p>
                    <p className="text-sm">{ledgerConfig.opening_balance?.created_by_name}</p>
                  </div>
                </div>
                
                {ledgerConfig?.opening_balance_locked ? (
                  <Alert className="mt-4 bg-orange-50 border-orange-200">
                    <Lock className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                      <strong>Locked:</strong> Opening Balance cannot be deleted because transactions exist after {CASH_LEDGER_START_DATE}. Contact your system administrator if you need to modify it.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteAdjustment(ledgerConfig.opening_balance?.id)}
                    >
                      Delete Opening Balance
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      You can delete because no transactions exist yet after the ledger start date.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-medium text-yellow-800">Opening Balance Not Set</h4>
                </div>
                <p className="text-sm text-yellow-700 mb-4">
                  You need to set the opening balance to start using the cash ledger. This should be the physical cash balance as of December 31, 2025.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Opening Balance Amount (₹) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter physical cash balance as of Dec 31, 2025"
                      value={newAdjustment.amount}
                      onChange={(e) => setNewAdjustment({ ...newAdjustment, amount: e.target.value })}
                      className="text-lg"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Input
                      placeholder="e.g., Physical cash count on Dec 31, 2025"
                      value={newAdjustment.notes}
                      onChange={(e) => setNewAdjustment({ ...newAdjustment, notes: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Button onClick={handleAddAdjustment} className="w-full bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Set Opening Balance
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Info Section */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-gray-600" />
                <h4 className="font-medium text-gray-700">How the Cash Ledger Works</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Opening Balance = Physical cash as of Dec 31, 2025</li>
                <li>Only transactions from {CASH_LEDGER_START_DATE} onwards are counted</li>
                <li>Current Balance = Opening + Cash Revenue − Cash Expenses − Handovers</li>
                <li>All prior transactions are ignored for balance calculation</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustmentDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashReconciliationReport;
